/**
 * 分组任务结果网格组件
 * 用于按原图或提示词分组展示结果，并提供预览、选择和重试能力
 */

import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import {
  AlertCircle,
  Ban,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  ImageIcon,
  Images,
  Loader2,
  MousePointerClick,
  RotateCcw,
  XCircle,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type {
  BatchTaskGroupedResultGroup,
  BatchTaskGroupedResultOutput,
} from '@/types/task'
import { TASK_RESULT_STATUS } from '@/types/task'

/**
 * 失败态辅助信息项
 */
interface FailureMetaItem {
  label: string
  value: string
}

interface GroupedTaskResultGridProps {
  groups: BatchTaskGroupedResultGroup[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  onRetry: (taskId: string) => void
  isRetrying?: boolean
  selectionMode?: boolean
}

function getStatusConfig(status: string): {
  icon: typeof CheckCircle
  className: string
  text: string
  iconSpin?: boolean
} {
  switch (status) {
    case TASK_RESULT_STATUS.QUEUED:
      return {
        icon: Clock,
        className: 'text-amber-500',
        text: '排队中',
      }
    case TASK_RESULT_STATUS.RUNNING:
      return {
        icon: Loader2,
        className: 'text-blue-500',
        text: '生成中',
        iconSpin: true,
      }
    case TASK_RESULT_STATUS.COMPLETED:
      return {
        icon: CheckCircle,
        className: 'text-emerald-500',
        text: '完成',
      }
    case TASK_RESULT_STATUS.FAILED:
      return {
        icon: XCircle,
        className: 'text-red-500',
        text: '失败',
      }
    case TASK_RESULT_STATUS.CANCELLED:
      return {
        icon: Ban,
        className: 'text-slate-500',
        text: '已取消',
      }
    default:
      return {
        icon: Clock,
        className: 'text-muted-foreground',
        text: '未知',
      }
  }
}

function getDisplayOutputIndex(
  output: BatchTaskGroupedResultOutput,
  fallbackIndex: number
): number {
  if (output.group_output_index > 0) {
    return output.group_output_index
  }
  return fallbackIndex + 1
}

function resolvePreviewOutputIndex(outputs: BatchTaskGroupedResultOutput[]): number {
  const completedIndex = outputs.findIndex(
    (output) => output.status === TASK_RESULT_STATUS.COMPLETED && output.output_url !== null && output.output_url !== ''
  )
  if (completedIndex >= 0) {
    return completedIndex
  }

  const runningIndex = outputs.findIndex((output) => output.status === TASK_RESULT_STATUS.RUNNING)
  if (runningIndex >= 0) {
    return runningIndex
  }

  const failedIndex = outputs.findIndex((output) => output.status === TASK_RESULT_STATUS.FAILED)
  if (failedIndex >= 0) {
    return failedIndex
  }

  return 0
}

/**
 * 格式化处理耗时
 * @param processTimeMs - 处理耗时（毫秒）
 * @returns 适合界面展示的耗时文本
 */
function formatProcessTime(processTimeMs: number | null): string | null {
  if (processTimeMs === null || Number.isNaN(processTimeMs) || processTimeMs <= 0) {
    return null
  }

  if (processTimeMs < 1000) {
    return `${String(processTimeMs)} ms`
  }

  if (processTimeMs < 60_000) {
    return `${String(Math.round(processTimeMs / 100) / 10)} 秒`
  }

  const minutes = Math.floor(processTimeMs / 60_000)
  const seconds = Math.round((processTimeMs % 60_000) / 1000)

  if (seconds === 0) {
    return `${String(minutes)} 分钟`
  }

  return `${String(minutes)} 分 ${String(seconds)} 秒`
}

function renderOutputMedia(
  output: BatchTaskGroupedResultOutput,
  outputIndex: number,
  imageClassName: string
): ReactElement {
  if (output.status === TASK_RESULT_STATUS.COMPLETED && output.output_url !== null && output.output_url !== '') {
    return (
      <img
        src={output.output_url}
        alt={`结果 ${String(outputIndex)}`}
        className={imageClassName}
      />
    )
  }

  if (output.status === TASK_RESULT_STATUS.RUNNING) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-blue-500">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-[11px] font-medium">生成中</span>
      </div>
    )
  }

  if (output.status === TASK_RESULT_STATUS.FAILED) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-red-500/5 text-red-500">
        <AlertCircle className="h-6 w-6" />
        <span className="text-[11px] font-medium">生成失败</span>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/50 text-muted-foreground">
      <ImageIcon className="h-6 w-6" />
      <span className="text-[11px] font-medium">待生成</span>
    </div>
  )
}

/**
 * 失败态预览面板属性
 */
interface FailureStatePanelProps {
  output: BatchTaskGroupedResultOutput
  outputIndex: number
}

/**
 * 失败态预览面板
 * @param props - 组件属性
 * @returns 失败态预览 ReactElement
 */
function FailureStatePanel({
  output,
  outputIndex,
}: FailureStatePanelProps): ReactElement {
  const processTimeLabel = formatProcessTime(output.process_time_ms)
  const promptContent =
    output.prompt !== undefined && output.prompt !== '' ? output.prompt : null
  const errorMessage =
    output.error ?? '任务执行失败，暂未返回更详细的错误信息。'
  const metaItems: FailureMetaItem[] = [
    {
      label: '结果序号',
      value: `第 ${String(outputIndex)} 张`,
    },
    {
      label: '当前状态',
      value: '生成失败',
    },
    output.server_name !== undefined && output.server_name !== ''
      ? {
          label: '执行节点',
          value: output.server_name,
        }
      : null,
    processTimeLabel !== null
      ? {
          label: '处理耗时',
          value: processTimeLabel,
        }
      : null,
  ].filter((item): item is FailureMetaItem => item !== null)

  return (
    <div className="flex h-full w-full items-center justify-center px-8 py-2 sm:px-12">
      <div className="grid w-full max-w-4xl gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
        <div className="overflow-hidden rounded-2xl border border-red-500/15 bg-background shadow-sm">
          <div className="border-b border-red-500/10 bg-gradient-to-br from-red-500/[0.08] via-background to-background px-5 py-5 sm:px-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 text-red-500 ring-1 ring-red-500/10">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="min-w-0 space-y-1">
                <span className="inline-flex items-center gap-1 rounded-full border border-red-500/15 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400">
                  <XCircle className="h-3.5 w-3.5" />
                  当前结果生成失败
                </span>
                <h3 className="text-base font-semibold text-foreground sm:text-lg">
                  这张结果没有成功生成可预览图片
                </h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  先查看错误原因，再决定是否重试当前结果或切换查看同组其它输出。
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6">
            <Alert
              variant="destructive"
              className="border border-red-500/15 bg-red-500/[0.04]"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>错误详情</AlertTitle>
              <AlertDescription className="text-sm leading-6">
                <p className="whitespace-pre-wrap break-words">{errorMessage}</p>
              </AlertDescription>
            </Alert>

            {promptContent !== null && (
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-xs font-medium text-muted-foreground">
                  关联提示词
                </p>
                <p
                  className="mt-2 line-clamp-4 text-sm leading-6 text-foreground/80"
                  title={promptContent}
                >
                  {promptContent}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border bg-background/95 p-4 shadow-sm">
          <div>
            <p className="text-xs font-medium text-muted-foreground/80">
              当前结果
            </p>
            <div className="mt-3 space-y-3">
              {metaItems.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5"
                >
                  <p className="text-[11px] text-muted-foreground">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-dashed border-border/70 bg-muted/15 p-3">
            <p className="text-sm font-medium text-foreground">可执行操作</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              点击下方“重试当前结果”重新发起这一项，也可以左右切换查看本组其它结果。
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function GroupSourceTile({ group }: { group: BatchTaskGroupedResultGroup }): ReactElement {
  const isTextToImage = group.generation_source_type === 'text_to_image'

  if (isTextToImage) {
    return (
      <div className="relative overflow-hidden rounded-lg border bg-muted/30">
        <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          提示词
        </div>
        <div className="flex aspect-square items-center p-3">
          <p
            className="line-clamp-6 text-xs leading-5 text-foreground"
            title={group.prompt ?? group.source_label}
          >
            {group.prompt ?? group.source_label ?? '未填写提示词'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-lg border bg-muted">
      <div className="absolute left-2 top-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
        原图
      </div>
      {group.input_url !== undefined && group.input_url !== '' ? (
        <img
          src={group.input_url}
          alt={group.source_label ?? '原图'}
          className="aspect-square w-full object-cover"
        />
      ) : (
        <div className="flex aspect-square items-center justify-center bg-muted/50 text-muted-foreground">
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
    </div>
  )
}

interface StackedOutputPreviewProps {
  outputs: BatchTaskGroupedResultOutput[]
  onOpen: () => void
}

function StackedOutputPreview({
  outputs,
  onOpen,
}: StackedOutputPreviewProps): ReactElement {
  const previewOutputIndex = resolvePreviewOutputIndex(outputs)
  const topOutput = outputs[previewOutputIndex]
  if (topOutput === undefined) {
    return (
      <div className="relative aspect-square overflow-hidden rounded-lg border bg-muted/30">
        <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          结果
        </div>
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <ImageIcon className="h-6 w-6" />
          <span className="text-[11px] font-medium">暂无结果</span>
        </div>
      </div>
    )
  }
  const hasMultiple = outputs.length > 1
  const stackLayers = Math.min(outputs.length, 3)
  const topOutputIndex = getDisplayOutputIndex(topOutput, previewOutputIndex)
  const statusConfig = getStatusConfig(topOutput.status)
  const StatusIcon = statusConfig.icon
  const showStatusBadge =
    topOutput.status !== TASK_RESULT_STATUS.QUEUED &&
    topOutput.status !== TASK_RESULT_STATUS.RUNNING

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative w-full text-left transition-transform hover:scale-[1.01]"
    >
      <div className="relative aspect-square">
        {Array.from({ length: stackLayers - 1 }, (_, index) => {
          const reverseIndex = stackLayers - index - 1
          return (
            <div
              key={`stack-${String(reverseIndex)}`}
              className="absolute inset-0 rounded-lg border bg-background shadow-sm"
              style={{
                transform: `translate(${String(reverseIndex * 5)}px, ${String(reverseIndex * 5)}px)`,
              }}
            />
          )
        })}

        <div className="absolute inset-0 overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="absolute left-2 top-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            结果
          </div>
          <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {hasMultiple && <Images className="h-3 w-3" />}
            <span>{String(outputs.length)}</span>
          </div>
          {showStatusBadge && (
            <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              <StatusIcon className={cn('h-3 w-3', statusConfig.iconSpin === true && 'animate-spin')} />
              <span>{statusConfig.text}</span>
            </div>
          )}
          {renderOutputMedia(topOutput, topOutputIndex, 'h-full w-full object-cover')}
        </div>
      </div>
    </button>
  )
}

interface OutputSelectionTileProps {
  output: BatchTaskGroupedResultOutput
  outputIndex: number
  isSelected: boolean
  onToggleSelect: () => void
  onRetry: () => void
  isRetrying: boolean
}

function OutputSelectionTile({
  output,
  outputIndex,
  isSelected,
  onToggleSelect,
  onRetry,
  isRetrying,
}: OutputSelectionTileProps): ReactElement {
  const statusConfig = getStatusConfig(output.status)
  const StatusIcon = statusConfig.icon
  const isCompleted = output.status === TASK_RESULT_STATUS.COMPLETED
  const isFailed = output.status === TASK_RESULT_STATUS.FAILED

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-card transition-all',
        isSelected && 'border-primary ring-1 ring-primary',
        isFailed && 'border-red-500/30 bg-red-500/5',
        isCompleted && 'cursor-pointer hover:border-primary/50'
      )}
      onClick={(): void => {
        if (isCompleted) {
          onToggleSelect()
        }
      }}
    >
      {isCompleted && (
        <div className="absolute left-2 top-2 z-10">
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-muted-foreground/50 bg-background/90'
            )}
          >
            {isSelected && <Check className="h-3 w-3" />}
          </div>
        </div>
      )}

      {isFailed && (
        <div className="absolute left-2 top-2 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(): void => {
              onToggleSelect()
            }}
            onClick={(event): void => {
              event.stopPropagation()
            }}
            className="h-5 w-5 border-2 bg-background/90"
          />
        </div>
      )}

      <div className="absolute right-2 top-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {String(outputIndex)}
      </div>

      <div className="aspect-square overflow-hidden bg-muted">
        {renderOutputMedia(output, outputIndex, 'h-full w-full object-cover')}
      </div>

      <div className="space-y-1 px-2 py-2">
        <div className={cn('flex items-center gap-1 text-[11px] font-medium', statusConfig.className)}>
          <StatusIcon className={cn('h-3.5 w-3.5', statusConfig.iconSpin === true && 'animate-spin')} />
          <span>{statusConfig.text}</span>
        </div>

        {output.prompt !== undefined && output.prompt !== '' && (
          <p className="line-clamp-1 text-[11px] text-muted-foreground" title={output.prompt}>
            P{String(output.prompt_index + 1)}: {output.prompt}
          </p>
        )}

        {isFailed && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1 h-7 w-full gap-1 px-2 text-[11px]"
            onClick={(event): void => {
              event.stopPropagation()
              onRetry()
            }}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5" />
            )}
            重试
          </Button>
        )}
      </div>
    </div>
  )
}

interface OutputCarouselDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: BatchTaskGroupedResultGroup
  outputs: BatchTaskGroupedResultOutput[]
  currentIndex: number
  onCurrentIndexChange: (index: number) => void
  onRetry: (taskId: string) => void
  isRetrying: boolean
}

function OutputCarouselDialog({
  open,
  onOpenChange,
  group,
  outputs,
  currentIndex,
  onCurrentIndexChange,
  onRetry,
  isRetrying,
}: OutputCarouselDialogProps): ReactElement {
  const safeCurrentIndex = outputs[currentIndex] !== undefined ? currentIndex : 0
  const currentOutput = outputs[safeCurrentIndex] ?? outputs[0]
  const dialogTitle = group.generation_source_type === 'text_to_image'
    ? `第 ${String(group.group_index + 1)} 组结果预览`
    : (group.source_label ?? '生成结果')
  if (currentOutput === undefined) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-[min(58vw,920px)] sm:max-w-[min(58vw,920px)]">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>暂无可展示的结果图</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }
  const currentOutputIndex = getDisplayOutputIndex(currentOutput, safeCurrentIndex)
  const statusConfig = getStatusConfig(currentOutput.status)
  const StatusIcon = statusConfig.icon
  const canGoPrev = safeCurrentIndex > 0
  const canGoNext = safeCurrentIndex < outputs.length - 1
  const isFailedOutput = currentOutput.status === TASK_RESULT_STATUS.FAILED

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[84vh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:h-[88vh] sm:w-[min(58vw,920px)] sm:max-w-[min(58vw,920px)]">
        <DialogHeader className="border-b px-5 py-3.5 sm:px-6">
          <DialogTitle className="flex items-center justify-between gap-3 pr-8">
            <span className="truncate text-base font-semibold">
              {dialogTitle}
            </span>
            <span className="shrink-0 text-sm text-muted-foreground">
              {String(safeCurrentIndex + 1)} / {String(outputs.length)}
            </span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <StatusIcon className={cn('h-4 w-4', statusConfig.className, statusConfig.iconSpin === true && 'animate-spin')} />
            <span>{statusConfig.text}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'relative flex min-h-0 flex-1 items-center justify-center bg-muted/20 px-4 py-3 sm:px-5 sm:py-4',
              isFailedOutput && 'overflow-y-auto'
            )}
          >
            <Button
              variant="outline"
              size="icon"
              className="absolute left-4 top-1/2 z-10 h-9 w-9 -translate-y-1/2"
              onClick={(): void => {
                if (canGoPrev) {
                  onCurrentIndexChange(safeCurrentIndex - 1)
                }
              }}
              disabled={!canGoPrev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {isFailedOutput ? (
              <FailureStatePanel
                output={currentOutput}
                outputIndex={currentOutputIndex}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl border bg-background shadow-sm">
                {renderOutputMedia(
                  currentOutput,
                  currentOutputIndex,
                  'max-h-full max-w-full object-contain'
                )}
              </div>
            )}

            <Button
              variant="outline"
              size="icon"
              className="absolute right-4 top-1/2 z-10 h-9 w-9 -translate-y-1/2"
              onClick={(): void => {
                if (canGoNext) {
                  onCurrentIndexChange(safeCurrentIndex + 1)
                }
              }}
              disabled={!canGoNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="border-t px-4 py-3">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {outputs.map((output, index) => {
                const outputIndex = getDisplayOutputIndex(output, index)
                const isActive = index === safeCurrentIndex

                return (
                  <button
                    key={output.task_id}
                    type="button"
                    onClick={(): void => {
                      onCurrentIndexChange(index)
                    }}
                    className={cn(
                      'relative shrink-0 overflow-hidden rounded-lg border transition-all',
                      isActive ? 'border-primary ring-1 ring-primary' : 'border-border'
                    )}
                  >
                    <div className="absolute right-1 top-1 z-10 rounded bg-black/60 px-1 py-0.5 text-[10px] font-medium text-white">
                      {String(outputIndex)}
                    </div>
                    <div className="h-16 w-16 overflow-hidden bg-muted">
                      {renderOutputMedia(output, outputIndex, 'h-full w-full object-cover')}
                    </div>
                  </button>
                )
              })}
            </div>

            {currentOutput.status !== TASK_RESULT_STATUS.FAILED &&
              currentOutput.error !== null &&
              currentOutput.error !== '' && (
                <p
                  className="mt-2 line-clamp-2 text-xs text-red-500"
                  title={currentOutput.error}
                >
                  {currentOutput.error}
                </p>
              )}

            {currentOutput.status === TASK_RESULT_STATUS.FAILED && (
              <div className="mt-3 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={(): void => {
                    onRetry(currentOutput.task_id)
                  }}
                  disabled={isRetrying}
                >
                  {isRetrying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  重试当前结果
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ResultGroupCardProps {
  group: BatchTaskGroupedResultGroup
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  onRetry: (taskId: string) => void
  isRetrying: boolean
  selectionMode: boolean
}

function ResultGroupCard({
  group,
  selectedIds,
  onSelectionChange,
  onRetry,
  isRetrying,
  selectionMode,
}: ResultGroupCardProps): ReactElement {
  const [carouselOpen, setCarouselOpen] = useState(false)
  const [activeOutputIndex, setActiveOutputIndex] = useState(0)

  const completedOutputs = useMemo(
    () => group.outputs.filter((output) => output.status === TASK_RESULT_STATUS.COMPLETED).length,
    [group.outputs]
  )

  const failedOutputs = useMemo(
    () => group.outputs.filter((output) => output.status === TASK_RESULT_STATUS.FAILED).length,
    [group.outputs]
  )

  const toggleSelect = (taskId: string): void => {
    if (selectedIds.includes(taskId)) {
      onSelectionChange(selectedIds.filter((id) => id !== taskId))
      return
    }
    onSelectionChange([...selectedIds, taskId])
  }

  return (
    <>
      <div className="rounded-xl border bg-card p-3 shadow-sm">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="truncate text-sm font-semibold text-foreground">
              {group.source_label ?? `分组 ${String(group.group_index + 1)}`}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span>{String(group.total_outputs)} 张输出</span>
              {completedOutputs > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400">
                  {String(completedOutputs)} 成功
                </span>
              )}
              {failedOutputs > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {String(failedOutputs)} 失败
                </span>
              )}
            </div>
          </div>

          {selectionMode && (
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-primary">
              <MousePointerClick className="h-3.5 w-3.5" />
              选图
            </span>
          )}
        </div>

        <div className={cn('grid gap-2', selectionMode ? 'grid-cols-2' : 'grid-cols-2')}>
          <GroupSourceTile group={group} />

          {selectionMode ? (
            <div className="grid grid-cols-2 gap-2">
              {group.outputs.map((output, index) => (
                <OutputSelectionTile
                  key={output.task_id}
                  output={output}
                  outputIndex={getDisplayOutputIndex(output, index)}
                  isSelected={selectedIds.includes(output.task_id)}
                  onToggleSelect={(): void => {
                    toggleSelect(output.task_id)
                  }}
                  onRetry={(): void => {
                    onRetry(output.task_id)
                  }}
                  isRetrying={isRetrying}
                />
              ))}
            </div>
          ) : (
            <StackedOutputPreview
              outputs={group.outputs}
              onOpen={(): void => {
                setActiveOutputIndex(resolvePreviewOutputIndex(group.outputs))
                setCarouselOpen(true)
              }}
            />
          )}
        </div>
      </div>

      {!selectionMode && (
        <OutputCarouselDialog
          open={carouselOpen}
          onOpenChange={setCarouselOpen}
          group={group}
          outputs={group.outputs}
          currentIndex={activeOutputIndex}
          onCurrentIndexChange={setActiveOutputIndex}
          onRetry={onRetry}
          isRetrying={isRetrying}
        />
      )}
    </>
  )
}

export function GroupedTaskResultGrid({
  groups,
  selectedIds,
  onSelectionChange,
  onRetry,
  isRetrying = false,
  selectionMode = false,
}: GroupedTaskResultGridProps): ReactElement {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
      {groups.map((group) => (
        <ResultGroupCard
          key={`${group.generation_source_type}-${String(group.group_index)}`}
          group={group}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          onRetry={onRetry}
          isRetrying={isRetrying}
          selectionMode={selectionMode}
        />
      ))}
    </div>
  )
}
